/**
 * ============================================================================
 * CORE CONFIGURATION: Platform Constants & Metadata Definitions
 * ============================================================================
 * Purpose:
 *   Centralized constants, role definitions, status badge color mappings,
 *   HTTP header keys for multi-tenancy, and API endpoints across the frontend.
 * ============================================================================
 */

/**
 * Helper to ensure protocol on URLs if omitted
 */
const ensureProtocol = (url, defaultProtocol = "https://") => {
  if (!url) return "";
  const trimmed = String(url).trim();
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }
  return `${defaultProtocol}${trimmed}`;
};

/**
 * Helper to detect if running on localhost / local development in browser
 */
const isBrowserLocalhost =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname === "0.0.0.0");

/**
 * Root Server Base URL from environment (e.g. "https://apis.digiforest.in" or "http://localhost:8005")
 * Strips any trailing "/v3", "/api/v3", "/api", or slashes so it is always a clean origin.
 */
export const SERVER_BASE_URL = ensureProtocol(
  (() => {
    if (isBrowserLocalhost) {
      const devServer = (
        process.env.REACT_APP_SERVER_URL ||
        process.env.REACT_APP_API_BASE_URL ||
        ""
      ).trim();
      if (devServer && (devServer.includes("localhost") || devServer.includes("127.0.0.1"))) {
        return devServer.replace(/\/(api\/)?v\d+\/?$/i, "").replace(/\/+$/, "");
      }
      return "http://localhost:8005";
    }

    return (
      process.env.REACT_APP_SERVER_URL ||
      process.env.REACT_APP_API_BASE_URL ||
      process.env.REACT_APP_API_URL ||
      "https://apis.digiforest.in"
    )
      .replace(/\/(api\/)?v\d+\/?$/i, "")
      .replace(/\/api\/?$/i, "")
      .replace(/\/+$/, "");
  })()
);

/**
 * Modern REST API URL (strictly "/v3" endpoint prefix)
 */
export const API_BASE_URL = ensureProtocol(
  (() => {
    if (isBrowserLocalhost) {
      const devApi = (process.env.REACT_APP_API_URL || "").trim();
      if (devApi && (devApi.includes("localhost") || devApi.includes("127.0.0.1"))) {
        const cleaned = devApi.replace(/\/+$/, "");
        return /\/v3$/i.test(cleaned) ? cleaned : `${cleaned}/v3`;
      }
      return `${SERVER_BASE_URL}/v3`;
    }

    const raw = (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || "").trim();
    if (raw) {
      const cleaned = raw.replace(/\/+$/, "");
      if (/\/v3$/i.test(cleaned)) {
        return cleaned;
      }
    }
    return `${SERVER_BASE_URL}/v3`;
  })()
);

/**
 * API Version Aliases for Documentation, Dispatches, and Webhooks
 */
export const API_V3_URL = `${SERVER_BASE_URL}/v3`;
export const API_V2_URL = API_V3_URL; // Alias for backward compatibility with components

/**
 * Frontend Panel Origin (e.g. "https://panel.digiforest.in" or "http://localhost:3000")
 */
export const FRONTEND_URL = ensureProtocol(
  (() => {
    if (isBrowserLocalhost) {
      return (typeof window !== "undefined" && window.location.origin) || "http://localhost:3000";
    }
    return (
      process.env.REACT_APP_FRONTEND_URL ||
      process.env.REACT_APP_PANEL_URL ||
      "https://panel.digiforest.in"
    ).replace(/\/+$/, "");
  })()
);

/**
 * WebSocket Server Host (e.g. "apis.digiforest.in" or "localhost:8005")
 */
export const WS_HOST =
  process.env.REACT_APP_WS_HOST ||
  SERVER_BASE_URL.replace(/^https?:\/\//, "");

/**
 * Canonical System Roles matching `tbl_roles` (Sheet 5)
 */
export const ROLES = {
  SUPER_ADMIN: 1,
  ADMIN: 2,
  USER: 3,
};

export const ROLE_LABELS = {
  1: "SuperAdmin",
  2: "Admin",
  3: "User",
};

/**
 * HTTP Header Keys used for resolving multi-tenant database context
 */
export const TENANT_HEADERS = {
  ACCOUNT_ID: "x-account-id",
  USER_ID: "x-user-id",
  DB_NAME: "x-db-name",
  TENANT_ID: "x-tenant-id",
};

/**
 * Storage Local Storage Keys
 */
export const STORAGE_KEYS = {
  TOKEN: "token",
  USER: "user",
  THEME: "theme",
  ACTIVE_ACCOUNT_ID: "active_account_id",
  ACTIVE_DB_NAME: "active_db_name",
};

/**
 * Status Color Pill Configuration for Badges
 */
export const STATUS_COLORS = {
  ACTIVE: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  INACTIVE: "bg-rose-500/10 text-rose-500 border-rose-500/20",
  PENDING: "bg-amber-500/10 text-amber-500 border-amber-500/20",
  SUSPENDED: "bg-slate-500/10 text-slate-400 border-slate-500/20",
  OPEN: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  RESOLVED: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  CLOSED: "bg-slate-500/10 text-slate-400 border-slate-500/20",
  DELIVERED: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  SENT: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  READ: "bg-purple-500/10 text-purple-500 border-purple-500/20",
  FAILED: "bg-rose-500/10 text-rose-500 border-rose-500/20",
  QUEUED: "bg-amber-500/10 text-amber-500 border-amber-500/20",
};
